/**
 * THE org role vocabulary, shared by the Convex backend and the web app.
 *
 * Membership roles were previously spelled out by hand at ~21 sites — Convex column and
 * argument validators, TypeScript unions, a rank map and two frontend label/option maps — with
 * nothing forcing them to agree. Everything now derives from {@link ORG_ROLE_VALUES}: the
 * TypeScript type, the Convex validator (`literalsToValidator(ORG_ROLE_VALUES)` in
 * `convex/lib/orgRoles.ts`), the rank used for "at least this role" checks, the display labels
 * and the dropdown options.
 *
 * This module is deliberately dependency-free so both runtimes can import it, including from a
 * Convex module the schema evaluates.
 */
/**
 * The three app-level org roles, in the canonical order every surface presents them:
 * most privileged first.
 *
 * The order is load-bearing in two ways. It is the member order of the derived Convex
 * validator — reordering it changes the emitted validator JSON and therefore the schema
 * fingerprint — and it is the order role dropdowns render. Settings and DF Admin previously
 * listed their role pickers in opposite orders; both now read {@link ORG_ROLE_OPTIONS}, so the
 * two agree on admin-first.
 */
export const ORG_ROLE_VALUES = ["app_org_admin", "app_org_reviewer", "app_org_member"];
/**
 * Privilege ordering, ascending: `app_org_member` < `app_org_reviewer` < `app_org_admin`.
 *
 * Compare ranks rather than testing role equality wherever a check means "at least this role".
 */
export const ORG_ROLE_RANK = {
    app_org_member: 1,
    app_org_reviewer: 2,
    app_org_admin: 3,
};
/** Human-readable role names. The one place the words "Admin"/"Reviewer"/"Member" are written. */
export const ORG_ROLE_LABELS = {
    app_org_admin: "Admin",
    app_org_reviewer: "Reviewer",
    app_org_member: "Member",
};
/** The canonical role-picker options: one ordering, one label, every dropdown. */
export const ORG_ROLE_OPTIONS = ORG_ROLE_VALUES.map((value) => ({ value, label: ORG_ROLE_LABELS[value] }));
/**
 * Does `role` carry at least the privilege of `minimum`?
 *
 * This is the one place "is this member an admin" style gating is expressed. Prefer it to
 * `role === "app_org_admin"`: an equality test silently means "exactly admin", so it answers the
 * wrong question the moment a role is inserted above the one being compared, and it re-spells a
 * role literal at every call site. Comparing {@link ORG_ROLE_RANK} keeps the intent — a privilege
 * floor — in the code.
 */
export function isAtLeastOrgRole(role, minimum) {
    return ORG_ROLE_RANK[role] >= ORG_ROLE_RANK[minimum];
}
/**
 * Is `role` an organization administrator?
 *
 * The one predicate behind every "can this member manage the org" gate. It is a privilege
 * floor, not an equality test — see {@link isAtLeastOrgRole} — so inserting a role above
 * `app_org_admin` keeps the answer right instead of silently locking the new role out.
 *
 * Nullish input answers `false`: a membership row that is absent grants nothing, which is
 * exactly what the `membership?.role === "app_org_admin"` sites it replaces already meant.
 */
export function isOrgAdminRole(role) {
    return role != null && isAtLeastOrgRole(role, "app_org_admin");
}
